import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { v4 as uuid } from "uuid";
import imageCompression from "browser-image-compression";

export interface SaveImageConfig {
  image: Blob;
  existingFilename?: string;
  newFilename?: string;
  isPublic: boolean;
  folder?: string;
  mimeTypeParam?: string;
}

export interface ImageTransformOptions {
  width?: number;
  height?: number;
  resize?: "cover" | "contain" | "fill";
  quality?: number;
}

// Pre-generated thumbnail sizes to avoid Supabase transform limits (100/month)
export const THUMBNAIL_SIZES = {
  "thumb-sm": 300, // Small previews, list items
  "thumb-md": 600, // Cards, standard displays
  "thumb-lg": 1200, // Large displays
} as const;

export type ThumbnailSize = keyof typeof THUMBNAIL_SIZES | "original";

export const useImageStoreRemote = () => {
  const { userID, client: supabase } = useAuthContext();

  const resizeAndSaveImage = async (
    config: SaveImageConfig,
    maxSize: number,
    mimeType: string
  ): Promise<string> => {
    const image = await resizeImageToMaxSize(config.image, maxSize, mimeType);
    const newConfig = {
      ...config,
      image,
    };
    return saveImage(newConfig);
  };

  const saveImage = async (config: SaveImageConfig): Promise<string> => {
    if (!userID)
      throw new Error("useImageRemoteStore 17: User not authenticated");
    if (!supabase) throw new Error("Supabase client not initialized");

    const id = uuid();
    const mimeType = config.mimeTypeParam || config.image.type || "image/png";
    const extension = mimeType === "image/jpeg" ? "jpeg" : "png";

    const pathPrefix = config.isPublic
      ? config.folder
        ? `${config.folder}/`
        : ""
      : config.folder
      ? `images/${userID}/${config.folder}/`
      : `images/${userID}/`;

    const newFilePath = `${pathPrefix}${config.newFilename ?? id}.${extension}`;
    const bucket = config.isPublic ? "public-images" : "user-images";

    // Only delete existing file if existingFilename is provided
    if (config.existingFilename) {
      const existingFilePath = `${pathPrefix}${config.existingFilename}.${extension}`;
      await supabase.storage.from(bucket).remove([existingFilePath]);
    }

    const { error } = await supabase.storage
      .from(bucket)
      .upload(newFilePath, config.image, {
        upsert: true, // still nice for safety, even though we delete
        contentType: mimeType,
      });

    return config.newFilename ?? id;
  };

  const resizeImageToMaxSize = async (
    image: Blob,
    maxSize: number,
    mimeType: string
  ): Promise<Blob> => {
    const options = {
      maxWidthOrHeight: maxSize,
      useWebWorker: true,
      fileType: `image/${mimeType}`,
    };

    const compressedFile = await imageCompression(image as File, options);
    return compressedFile;
  };

  // Save an image along with pre-generated thumbnail sizes
  const saveImageWithThumbnails = async (
    config: SaveImageConfig
  ): Promise<string> => {
    // Save the original image first
    const originalId = await saveImage(config);

    const mimeType = config.mimeTypeParam || config.image.type || "image/png";
    const extension = mimeType === "image/jpeg" ? "jpeg" : "png";

    // Generate and save each thumbnail size
    for (const [sizeName, maxDimension] of Object.entries(THUMBNAIL_SIZES)) {
      try {
        const resizedBlob = await resizeImageToMaxSize(
          config.image,
          maxDimension,
          extension
        );

        const thumbConfig: SaveImageConfig = {
          ...config,
          image: resizedBlob,
          newFilename: `${originalId}_${sizeName}`,
          existingFilename: config.existingFilename
            ? `${config.existingFilename}_${sizeName}`
            : undefined,
        };

        await saveImage(thumbConfig);
      } catch (error) {
        console.error(`Failed to generate ${sizeName} thumbnail:`, error);
      }
    }

    return originalId;
  };

  const getImageUrl = async (
    id: string,
    extension: string,
    isPublic: boolean,
    folder?: string,
    transform?: ImageTransformOptions
  ): Promise<string | null> => {
    if (!supabase) throw new Error("Supabase client not initialized");

    const filePath = isPublic
      ? folder
        ? `${folder}/${id}.${extension}`
        : `${id}.${extension}`
      : folder
      ? `images/${userID}/${folder}/${id}.${extension}`
      : `images/${userID}/${id}.${extension}`;

    if (isPublic) {
      const { data } = supabase.storage
        .from("public-images")
        .getPublicUrl(filePath, transform ? { transform } : undefined);
      const url = data?.publicUrl || null;
      return url;
    }

    // if (!userID)
    //   throw new Error("useImageRemoteStore 62: User not authenticated");

    const { data, error } = await supabase.storage
      .from("user-images")
      .createSignedUrl(
        filePath,
        60 * 60 * 24,
        transform ? { transform } : undefined
      ); // 24 hours

    if (error || !data) return null;
    return data.signedUrl;
  };

  // Get URL for a pre-generated thumbnail size (avoids Supabase transform limits)
  const getImageUrlWithSize = async (
    id: string,
    extension: string,
    isPublic: boolean,
    folder?: string,
    size?: ThumbnailSize
  ): Promise<string | null> => {
    // If no size or "original" requested, return the original image
    if (!size || size === "original") {
      return getImageUrl(id, extension, isPublic, folder);
    }

    // Try to get the pre-generated thumbnail
    const thumbnailId = `${id}_${size}`;
    const url = await getImageUrl(thumbnailId, extension, isPublic, folder);

    if (url) {
      return url;
    }

    // Fallback to original if thumbnail doesn't exist
    console.warn(
      `Pre-generated thumbnail ${thumbnailId} not found, falling back to original`
    );
    return getImageUrl(id, extension, isPublic, folder);
  };

  // Delete an image and all its pre-generated thumbnails
  const deleteImageWithThumbnails = async (
    id: string,
    extension: string,
    isPublic: boolean,
    folder?: string
  ): Promise<boolean> => {
    if (!supabase) throw new Error("Supabase client not initialized");

    const bucket = isPublic ? "public-images" : "user-images";
    const pathPrefix = isPublic
      ? folder
        ? `${folder}/`
        : ""
      : folder
      ? `images/${userID}/${folder}/`
      : `images/${userID}/`;

    // Build list of files to delete: original + all thumbnail sizes
    const filesToDelete = [
      `${pathPrefix}${id}.${extension}`,
      ...Object.keys(THUMBNAIL_SIZES).map(
        (sizeName) => `${pathPrefix}${id}_${sizeName}.${extension}`
      ),
    ];

    const { error } = await supabase.storage.from(bucket).remove(filesToDelete);

    if (error) {
      console.error("Error deleting image with thumbnails:", error);
      return false;
    }

    return true;
  };

  const getRenderedResultImageURL = async (projectID: string) => {
    const imageURL = await getImageUrl(
      "result",
      "png",
      false,
      `renders/${projectID}`
    );
    //Now I want to call supabase to get
    if (!imageURL) {
      console.error("Image URL is undefined");
      return;
    }
    return imageURL;
  };

  const getRenderedThumbnailImageURL = async (projectID: string) => {
    const imageURL = await getImageUrl(
      "thumbnail",
      "png",
      false,
      `renders/${projectID}`
    );
    //Now I want to call supabase to get
    if (!imageURL) {
      console.error("Image URL is undefined");
      return;
    }
    return imageURL;
  };

  const duplicatePublicImage = async (id: string, extension: string) => {
    if (!supabase) throw new Error("Supabase client not initialized");

    const newID = uuid();

    const { data, error } = await supabase.storage
      .from("public-images")
      .copy(`${id}.${extension}`, `${newID}.${extension}`);

    if (error) {
      console.error("Error duplicating image:", error);
      return null;
    }

    return newID;
  };

  // Supply the project ID to save the image to `/project/${path}/${id}.{extension}`
  const copyImageFromPublicToUser = async (
    id: string,
    path: string,
    extension: string,
    projectID?: string
  ): Promise<string> => {
    if (!userID) throw new Error("useImageRemoteStore: User not authenticated");
    if (!supabase) throw new Error("Supabase client not initialized");

    const { data, error: downloadError } = await supabase.storage
      .from("public-images")
      .download(`${path}/${id}.${extension}`);

    if (downloadError) throw downloadError;

    const uploadPath = projectID
      ? `images/${userID}/projects/${projectID}/${path}/${id}.${extension}`
      : `images/${userID}/${path}/${id}.${extension}`;

    const { error: uploadError } = await supabase.storage
      .from("user-images")
      .upload(uploadPath, data, { upsert: true });

    if (uploadError) throw uploadError;

    return id;
  };

  const duplicateUserImage = async (
    originPath: string,
    filename: string,
    extension: string,
    destinationPath: string,
    destinationName: string
  ): Promise<string | undefined> => {
    if (!userID) throw new Error("useImageRemoteStore: User not authenticated");
    if (!supabase) throw new Error("Supabase client not initialized");

    const originFilePath = `images/${userID}/${originPath}/${filename}.${extension}`;
    const destinationFilePath = `images/${userID}/${destinationPath}/${destinationName}.${extension}`;
    console.log("Destination path is ", destinationFilePath);
    await supabase.storage.from("user-images").remove([destinationFilePath]);

    const { error } = await supabase.storage
      .from("user-images")
      .copy(originFilePath, destinationFilePath);

    if (error) {
      console.error("Error deleting image:", error);
      return undefined;
    }

    return destinationName;
  };

  const deletePointIconImage = async (projectID: string, imageName: string) => {
    if (!userID) throw new Error("useImageRemoteStore: User not authenticated");
    if (!supabase) throw new Error("Supabase client not initialized");

    const filePath = `images/${userID}/projects/${projectID}/pointIcons/${imageName}.png`;
    const { error } = await supabase.storage
      .from("user-images")
      .remove([filePath]);

    if (error) {
      console.error("Error deleting point icon image:", error);
      return false;
    }

    return true;
  };

  const deleteImage = async (
    id: string,
    path: string,
    extension: string,
    isPublic: boolean
  ) => {
    if (!supabase) throw new Error("Supabase client not initialized");

    const bucket = isPublic ? "public-images" : "user-images";
    const filePath = isPublic
      ? `${path}/${id}.${extension}`
      : `images/${userID}/${path}/${id}.${extension}`;

    const { error } = await supabase.storage.from(bucket).remove([filePath]);

    if (error) {
      console.error("Error deleting image:", error);
      return false;
    }

    return true;
  };

  const deleteAllFilesUnderUserPath = async (path: string) => {
    if (!userID) throw new Error("useImageRemoteStore: User not authenticated");
    if (!supabase) throw new Error("Supabase client not initialized");

    const bucket = "user-images";
    const fullPath = `images/${userID}/${path}`;

    console.log("Delete full path   is", fullPath);
    // First try removing as a single file
    // let { error } = await supabase.storage.from(bucket).remove([fullPath]);

    // if (error?.message.includes("Object not found")) {
    //   // Try treating it as a folder: list and delete contents
    const { data, error: listError } = await supabase.storage
      .from(bucket)
      .list(fullPath);

    if (listError) {
      console.error("Error listing folder contents:", listError);
      return false;
    }

    const filesToDelete = data.map((f) => `${fullPath}/${f.name}`);
    console.log("Files to delete:", filesToDelete);
    if (filesToDelete.length === 0) return true;

    const { error: deleteError } = await supabase.storage
      .from(bucket)
      .remove(filesToDelete);

    if (deleteError) {
      console.error("Error deleting folder contents:", deleteError);
      return false;
    }

    return true;
    //  }

    // if (error) {
    //   console.error("Error deleting file:", error);
    //   return false;
    // }

    // return true;
  };

  return {
    saveImage,
    saveImageWithThumbnails,
    resizeAndSaveImage,
    getImageUrl,
    getImageUrlWithSize,
    duplicatePublicImage,
    duplicateUserImage,
    getRenderedResultImageURL,
    getRenderedThumbnailImageURL,
    copyImageFromPublicToUser,
    deleteImage,
    deleteImageWithThumbnails,
    deletePointIconImage,
    deleteAllFilesUnderUserPath,
  };
};
